/**
 * Prompt Library Store - Web Version
 *
 * Manages prompt templates (user, org, and system) with persistence.
 * Follows the project's Zustand architecture pattern:
 * - Store handles ONLY state management
 * - React hooks orchestrate business logic
 * - No API calls or cross-store dependencies
 *
 * NOTE: 100% browser-compatible, no Electron dependencies!
 */

import type { PromptTemplate, SuggestionCache } from '@ctrl-enter/types';
import { SYSTEM_PROMPT_TEMPLATES } from '@ctrl-enter/types';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

// ============================================================================
// STATE TYPE
// ============================================================================

type PromptLibraryState = {
  // User prompts (created by current user, server-backed via WorkOS)
  userPrompts: PromptTemplate[];

  // Organization prompts (deprecated, kept for future use)
  orgPrompts: PromptTemplate[];

  // Favorites (user's favorite prompt IDs)
  favoriteIds: Set<string>;

  // Recently used prompt IDs (for quick access)
  recentlyUsedIds: string[];

  // Usage tracking (prompt ID -> count)
  usageCounts: Record<string, number>;

  // Dynamic suggestion cache
  suggestionCache: SuggestionCache | null;

  // UI state
  isLibraryModalOpen: boolean;
  isSuggestionsLoading: boolean;
  isPromptsLoading: boolean;
  isPreferencesLoading: boolean;

  // Actions - User Prompts (server-backed)
  setUserPrompts: (prompts: PromptTemplate[]) => void; // Load from server
  addUserPrompt: (
    prompt: Omit<PromptTemplate, 'id' | 'createdAt' | 'updatedAt' | 'source'> | PromptTemplate
  ) => void;
  updateUserPrompt: (id: string, updates: Partial<PromptTemplate>) => void;
  deleteUserPrompt: (id: string) => void;

  // Actions - Org Prompts (deprecated, kept for future use)
  setOrgPrompts: (prompts: PromptTemplate[]) => void;
  addOrgPrompt: (prompt: PromptTemplate) => void;
  updateOrgPrompt: (id: string, updates: Partial<PromptTemplate>) => void;
  removeOrgPrompt: (id: string) => void;

  // Actions - Favorites
  toggleFavorite: (promptId: string) => void;
  isFavorite: (promptId: string) => boolean;

  // Actions - Usage Tracking
  trackUsage: (promptId: string) => void;
  getUsageCount: (promptId: string) => number;

  // Actions - Recently Used
  addToRecentlyUsed: (promptId: string) => void;

  // Actions - Server Sync
  setPreferencesFromServer: (prefs: {
    favoriteIds: string[];
    recentlyUsedIds: string[];
    usageCounts: Record<string, number>;
  }) => void;

  // Actions - Suggestions Cache
  setSuggestionCache: (cache: SuggestionCache | null) => void;
  clearSuggestionCache: () => void;
  getSuggestionCache: () => SuggestionCache | null;

  // Actions - UI State
  openLibraryModal: () => void;
  closeLibraryModal: () => void;
  setIsSuggestionsLoading: (loading: boolean) => void;
  setIsPromptsLoading: (loading: boolean) => void;
  setIsPreferencesLoading: (loading: boolean) => void;

  // Actions - Data Management
  clearAllData: () => void;
};

// ============================================================================
// HELPERS
// ============================================================================

function generatePromptId(): string {
  return `prompt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

const MAX_RECENTLY_USED = 10;

// ============================================================================
// STORE
// ============================================================================

export const usePromptLibraryStore = create<PromptLibraryState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        userPrompts: [],
        orgPrompts: [],
        favoriteIds: new Set(),
        recentlyUsedIds: [],
        usageCounts: {},
        suggestionCache: null,
        isLibraryModalOpen: false,
        isSuggestionsLoading: false,
        isPromptsLoading: false,
        isPreferencesLoading: false,

        // User Prompts
        setUserPrompts: prompts =>
          set(() => ({
            userPrompts: prompts,
          })),

        addUserPrompt: promptData =>
          set(state => {
            // If already a full PromptTemplate (from server), use as-is
            if ('id' in promptData && 'createdAt' in promptData) {
              return {
                userPrompts: [...state.userPrompts, promptData as PromptTemplate],
              };
            }

            // Otherwise, generate ID and timestamps for new local prompt
            // (This shouldn't happen in prod since prompts are created via server)
            const newPrompt: PromptTemplate = {
              ...promptData,
              id: generatePromptId(),
              source: 'user',
              createdAt: Date.now(),
              updatedAt: Date.now(),
            };

            // Track prompt created
            import('../services/analytics').then(({ mixpanel }) => {
              mixpanel.track('Prompt Created', {
                category: newPrompt.category,
                hasVariables: (newPrompt.variables?.length || 0) > 0,
                variableCount: newPrompt.variables?.length || 0,
              });
            });

            return {
              userPrompts: [...state.userPrompts, newPrompt],
            };
          }),

        updateUserPrompt: (id, updates) =>
          set(state => {
            // Track prompt updated
            import('../services/analytics').then(({ mixpanel }) => {
              mixpanel.track('Prompt Updated', {
                promptId: id,
                updatedFields: Object.keys(updates),
              });
            });

            return {
              userPrompts: state.userPrompts.map(prompt =>
                prompt.id === id ? { ...prompt, ...updates, updatedAt: Date.now() } : prompt
              ),
            };
          }),

        deleteUserPrompt: id =>
          set(state => {
            // Track prompt deleted
            import('../services/analytics').then(({ mixpanel }) => {
              mixpanel.track('Prompt Deleted', {
                promptId: id,
              });
            });

            return {
              userPrompts: state.userPrompts.filter(p => p.id !== id),
              favoriteIds: new Set(Array.from(state.favoriteIds).filter(fid => fid !== id)),
              recentlyUsedIds: state.recentlyUsedIds.filter(rid => rid !== id),
            };
          }),

        // Org Prompts
        setOrgPrompts: prompts =>
          set(() => ({
            orgPrompts: prompts,
          })),

        addOrgPrompt: prompt =>
          set(state => ({
            orgPrompts: [...state.orgPrompts, prompt],
          })),

        updateOrgPrompt: (id, updates) =>
          set(state => ({
            orgPrompts: state.orgPrompts.map(prompt =>
              prompt.id === id ? { ...prompt, ...updates, updatedAt: Date.now() } : prompt
            ),
          })),

        removeOrgPrompt: id =>
          set(state => ({
            orgPrompts: state.orgPrompts.filter(p => p.id !== id),
            favoriteIds: new Set(Array.from(state.favoriteIds).filter(fid => fid !== id)),
            recentlyUsedIds: state.recentlyUsedIds.filter(rid => rid !== id),
          })),

        // Favorites
        toggleFavorite: promptId =>
          set(state => {
            const newFavorites = new Set(state.favoriteIds);
            if (newFavorites.has(promptId)) {
              newFavorites.delete(promptId);
            } else {
              newFavorites.add(promptId);
            }
            return { favoriteIds: newFavorites };
          }),

        isFavorite: promptId => {
          const state = get();
          return state.favoriteIds.has(promptId);
        },

        // Usage Tracking
        trackUsage: promptId =>
          set(state => ({
            usageCounts: {
              ...state.usageCounts,
              [promptId]: (state.usageCounts[promptId] || 0) + 1,
            },
          })),

        getUsageCount: promptId => {
          const state = get();
          return state.usageCounts[promptId] || 0;
        },

        // Recently Used
        addToRecentlyUsed: promptId =>
          set(state => {
            const filtered = state.recentlyUsedIds.filter(id => id !== promptId);
            const updated = [promptId, ...filtered].slice(0, MAX_RECENTLY_USED);
            return { recentlyUsedIds: updated };
          }),

        // Suggestion Cache
        setSuggestionCache: cache =>
          set(() => ({
            suggestionCache: cache,
          })),

        clearSuggestionCache: () =>
          set(() => ({
            suggestionCache: null,
          })),

        getSuggestionCache: () => {
          return get().suggestionCache;
        },

        // UI State
        openLibraryModal: () =>
          set(() => ({
            isLibraryModalOpen: true,
          })),

        closeLibraryModal: () =>
          set(() => ({
            isLibraryModalOpen: false,
          })),

        setIsSuggestionsLoading: loading =>
          set(() => ({
            isSuggestionsLoading: loading,
          })),

        setIsPromptsLoading: loading =>
          set(() => ({
            isPromptsLoading: loading,
          })),

        setIsPreferencesLoading: loading =>
          set(() => ({
            isPreferencesLoading: loading,
          })),

        // Server Sync
        setPreferencesFromServer: prefs =>
          set(() => ({
            favoriteIds: new Set(prefs.favoriteIds),
            recentlyUsedIds: prefs.recentlyUsedIds,
            usageCounts: prefs.usageCounts,
          })),

        // Data Management
        clearAllData: () =>
          set(() => ({
            userPrompts: [],
            orgPrompts: [],
            favoriteIds: new Set(),
            recentlyUsedIds: [],
            usageCounts: {},
            suggestionCache: null,
            isLibraryModalOpen: false,
            isSuggestionsLoading: false,
            isPromptsLoading: false,
            isPreferencesLoading: false,
          })),
      }),
      {
        name: 'prompt-library-store',
        // Custom serialization for Set
        partialize: state => ({
          // userPrompts NOT persisted (loaded from WorkOS server)
          // orgPrompts NOT persisted (deprecated, for future use)

          // User preferences (localStorage only for v1, could be server-backed in future)
          favoriteIds: Array.from(state.favoriteIds),
          recentlyUsedIds: state.recentlyUsedIds,
          usageCounts: state.usageCounts,

          // suggestionCache NOT persisted (short-lived, context-dependent)
        }),
        // Proper deserialization with type safety
        merge: (persistedState, currentState) => {
          const persisted = persistedState as any;
          return {
            ...currentState,
            // Don't restore userPrompts from localStorage - they come from server
            favoriteIds: new Set(persisted?.favoriteIds || []),
            recentlyUsedIds: persisted?.recentlyUsedIds || [],
            usageCounts: persisted?.usageCounts || {},
          };
        },
      }
    )
  )
);

// ============================================================================
// SELECTORS (Atomic for performance)
// ============================================================================

// Get user prompts only
export const useUserPrompts = () => usePromptLibraryStore(state => state.userPrompts);

// Get org prompts only
export const useOrgPrompts = () => usePromptLibraryStore(state => state.orgPrompts);

// Get system prompts only (constant, safe to use directly)
export const useSystemPrompts = () => SYSTEM_PROMPT_TEMPLATES;

// Get UI state
export const useIsLibraryModalOpen = () => usePromptLibraryStore(state => state.isLibraryModalOpen);

export const useIsSuggestionsLoading = () =>
  usePromptLibraryStore(state => state.isSuggestionsLoading);

export const useIsPromptsLoading = () => usePromptLibraryStore(state => state.isPromptsLoading);

export const useIsPreferencesLoading = () =>
  usePromptLibraryStore(state => state.isPreferencesLoading);
